// src/pages/Home.tsx
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Users,
  BookOpen,
  Award,
  Star,
  Sparkles,
  GraduationCap,
  ChevronRight,
  Quote,
  MessageCircle,
  Video,
  Briefcase,
  Globe,
  Smartphone,
  BarChart,
  LucideIcon,
  TrendingUp,
  CheckCircle,
  Play,
  Zap,
  Target,
  Shield,
  Heart,
  Code,
  Database,
  Cloud,
  Lock,
} from "lucide-react";
import SEO from "../components/SEO";
import Courses from "../sections/Courses";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Types
interface Stat {
  value: string;
  label: string;
  icon: LucideIcon;
  description: string;
}

interface Feature {
  title: string;
  description: string;
  icon: LucideIcon;
  link: string;
  color: string;
}

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  avatar: string;
  content: string;
  rating: number;
}

const Home = () => {
  const navigate = useNavigate();
  const statsRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // Stats Data
  const stats: Stat[] = [
    {
      value: "50,000+",
      label: "Active Students",
      icon: Users,
      description: "Growing community",
    },
    {
      value: "200+",
      label: "Expert Courses",
      icon: BookOpen,
      description: "In-demand topics",
    },
    {
      value: "100+",
      label: "Expert Mentors",
      icon: GraduationCap,
      description: "Industry leaders",
    },
    {
      value: "98%",
      label: "Success Rate",
      icon: TrendingUp,
      description: "Career outcomes",
    },
    {
      value: "4.9",
      label: "Average Rating",
      icon: Star,
      description: "From 10k+ reviews",
    },
    {
      value: "30+",
      label: "Countries",
      icon: Globe,
      description: "Worldwide reach",
    },
  ];

  // Features Data
  const features: Feature[] = [
    {
      title: "Expert-Led Courses",
      description:
        "Learn from industry professionals with real-world experience.",
      icon: BookOpen,
      link: "/courses",
      color: "from-blue-500 to-blue-600",
    },
    {
      title: "1-on-1 Mentorship",
      description: "Connect with mentors for personalized guidance.",
      icon: Users,
      link: "/mentorship",
      color: "from-orange-500 to-orange-600",
    },
    {
      title: "Live Video Sessions",
      description: "Join interactive video calls with Zoom Meet.",
      icon: Video,
      link: "/mentorship",
      color: "from-green-500 to-green-600",
    },
    {
      title: "Community Forums",
      description: "Engage with a global community of learners.",
      icon: MessageCircle,
      link: "/forums",
      color: "from-purple-500 to-purple-600",
    },
    {
      title: "Verified Certificates",
      description: "Earn shareable certificates upon completion.",
      icon: Award,
      link: "/my-certificates",
      color: "from-yellow-500 to-yellow-600",
    },
    {
      title: "Progress Tracking",
      description: "Monitor your learning journey with detailed analytics.",
      icon: BarChart,
      link: "/dashboard",
      color: "from-pink-500 to-pink-600",
    },
    {
      title: "Mobile Learning",
      description: "Learn anytime, anywhere on any device.",
      icon: Smartphone,
      link: "/courses",
      color: "from-indigo-500 to-indigo-600",
    },
    {
      title: "Career Resources",
      description: "Access resume templates and interview guides.",
      icon: Briefcase,
      link: "/resources",
      color: "from-red-500 to-red-600",
    },
  ];

  // Testimonials
  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "John Anderson",
      role: "Software Engineer",
      company: "Google",
      avatar: "/avatar-1.jpg",
      content:
        "This platform transformed my career. The mentorship program helped me land my dream job at Google. The courses are practical and industry-relevant.",
      rating: 5,
    },
    {
      id: 2,
      name: "Maria Garcia",
      role: "Data Scientist",
      company: "Microsoft",
      avatar: "/avatar-2.jpg",
      content:
        "The data science course was comprehensive and well-structured. The instructors are knowledgeable and always ready to help.",
      rating: 5,
    },
    {
      id: 3,
      name: "David Kim",
      role: "Product Manager",
      company: "Amazon",
      avatar: "/avatar-3.jpg",
      content:
        "Excellent platform for career advancement. The mentorship sessions provided invaluable insights.",
      rating: 5,
    },
    {
      id: 4,
      name: "Lisa Wang",
      role: "Frontend Developer",
      company: "Meta",
      avatar: "/avatar-4.jpg",
      content:
        "The web development bootcamp was intense but rewarding. The projects prepared me well for real-world challenges.",
      rating: 5,
    },
  ];

  // GSAP Animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".hero-title",
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", delay: 0.2 },
      );
      gsap.fromTo(
        ".hero-subtitle",
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, ease: "power3.out", delay: 0.4 },
      );
      gsap.fromTo(
        ".hero-buttons",
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.4, ease: "power3.out", delay: 0.6 },
      );
      gsap.fromTo(
        ".hero-image",
        { opacity: 0, x: 30 },
        { opacity: 1, x: 0, duration: 0.6, ease: "power3.out", delay: 0.3 },
      );

      gsap.fromTo(
        ".stat-card",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.05,
          ease: "power3.out",
          scrollTrigger: { trigger: statsRef.current, start: "top 85%" },
        },
      );

      gsap.fromTo(
        ".feature-card",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.04,
          ease: "power3.out",
          scrollTrigger: { trigger: featuresRef.current, start: "top 85%" },
        },
      );
    });
    return () => ctx.revert();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-2.5 h-2.5 md:w-3 md:h-3 ${star <= Math.floor(rating) ? "text-amber-400 fill-amber-400" : "text-gray-300"}`}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <SEO
        title="LearnHub Institute - Online Learning Platform | Expert Courses & Mentorship"
        description="Join 50,000+ learners mastering in-demand skills. Expert-led courses in web development, data science, cybersecurity, and more."
        keywords="online learning, tech courses, web development, data science, programming, mentorship"
      />
      <main className="min-h-screen overflow-x-hidden bg-white">
        {/* Hero Section */}
        <section className="relative pt-16 pb-12 overflow-hidden bg-gradient-to-br from-orange-50 via-orange-100/30 to-white md:pt-20 md:pb-16 lg:pt-24 lg:pb-20">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute rounded-full -top-40 -right-40 w-60 h-60 md:w-80 md:h-80 bg-orange-500/10 blur-3xl animate-pulse" />
            <div className="absolute rounded-full -bottom-40 -left-40 w-60 h-60 md:w-80 md:h-80 bg-orange-600/10 blur-3xl animate-pulse animation-delay-1000" />

            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="absolute w-0.5 h-0.5 rounded-full bg-orange-500/30 animate-float hidden md:block"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 4}s`,
                }}
              />
            ))}
          </div>

          <div className="relative z-10 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            {/* CHANGED: flex-col on mobile (image below text), lg:grid on desktop */}
            <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-5">
              {/* Left Column - Content */}
              <div>
                <div className="inline-flex items-center gap-1.5 px-1 py-1 mb-4 bg-gradient-to-r from-orange-500/10 to-orange-600/10 rounded-full border border-orange-200/30 md:px-3 md:py-1.5 md:mb-6">
                  <Sparkles className="w-3 h-3 text-orange-500 animate-pulse md:w-3.5 md:h-3.5" />
                  <span className="text-[10px] font-semibold text-orange-600 md:text-xs">
                    #1 Learning Platform 2026
                  </span>
                </div>

                <h1 className="text-2xl font-bold tracking-tight text-gray-900 hero-title sm:text-3xl md:text-4xl lg:text-5xl">
                  Learn, Grow, and
                  <br />
                  <span className="relative inline-block">
                    <span className="relative text-transparent bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text">
                      Level Up
                    </span>
                  </span>
                  <br />
                  Your Career
                </h1>

                <p className="max-w-md mt-4 text-base leading-relaxed text-gray-600 hero-subtitle sm:text-lg md:mt-6">
                  Join 50,000+ learners mastering in-demand skills with
                  expert-led courses, personalized mentorship, and
                  career-focused programs.
                </p>

                <div className="flex flex-wrap items-center gap-3 mt-5 hero-buttons md:mt-8">
                  <Link
                    to="/courses"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-white transition-all shadow-lg bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl hover:shadow-xl hover:scale-105 md:px-6 md:py-3"
                  >
                    Start Learning Now
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                  <Link
                    to="/mentorship"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-md md:px-6 md:py-3"
                  >
                    Find a Mentor
                    <Users className="w-4 h-4 transition-transform group-hover:scale-110" />
                  </Link>
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap items-center gap-4 mt-5 md:mt-8">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="w-6 h-6 border-2 border-white rounded-full shadow-sm bg-gradient-to-br from-gray-300 to-gray-400 md:w-8 md:h-8"
                      />
                    ))}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 md:text-base">
                      Trusted by 50,000+ learners
                    </p>
                    <div className="flex items-center gap-0.5 mt-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          className="w-3.5 h-3.5 text-amber-400 fill-amber-400"
                        />
                      ))}
                      <span className="ml-1 text-xs text-gray-500">4.9</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Hero Image (moves below text on mobile) */}
              <div className="relative hero-image lg:order-last">
                <div className="relative overflow-hidden shadow-xl bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl">
                  <img
                    src="/he.png"
                    alt="Learning illustration"
                    className="relative z-10 object-contain w-full h-[220px] sm:h-[280px] md:h-[320px] lg:h-[350px]"
                  />

                  {/* Floating Badges - Hidden on mobile, show on tablet+ */}
                  <div className="absolute z-20 p-1.5 bg-white rounded-lg shadow-lg top-2 -right-1 animate-bounce-slow hidden sm:block md:p-2 md:top-4 md:-right-2">
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <div className="flex items-center justify-center w-6 h-6 bg-green-100 rounded-full md:w-8 md:h-8">
                        <CheckCircle className="w-3 h-3 text-green-600 md:w-4 md:h-4" />
                      </div>
                      <div>
                        <p className="text-[9px] font-bold text-gray-900 md:text-xs">
                          Course Completed
                        </p>
                        <p className="text-[7px] text-gray-500 md:text-[9px]">
                          +45% progress
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute z-20 p-1.5 bg-white rounded-lg shadow-lg bottom-2 -left-1 animate-bounce-slow animation-delay-500 hidden sm:block md:p-2 md:bottom-4 md:-left-2">
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <div className="flex items-center justify-center w-6 h-6 bg-orange-100 rounded-full md:w-8 md:h-8">
                        <Users className="w-3 h-3 text-orange-600 md:w-4 md:h-4" />
                      </div>
                      <div>
                        <p className="text-[9px] font-bold text-gray-900 md:text-xs">
                          Active Students
                        </p>
                        <p className="text-[7px] text-gray-500 md:text-[9px]">
                          50,000+ enrolled
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Wave Divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg
              className="w-full h-8 text-white md:h-12"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              fill="currentColor"
            >
              <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" />
            </svg>
          </div>
        </section>

        {/* Stats Section - Improved typography */}
        <section
          ref={statsRef}
          className="py-8 bg-white border-gray-100 border-y md:py-12"
        >
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 md:gap-4">
              {stats.map((stat, idx) => (
                <div key={idx} className="p-2 text-center stat-card md:p-3">
                  <div className="flex items-center justify-center w-8 h-8 mx-auto mb-1.5 bg-orange-100 rounded-lg md:w-10 md:h-10 md:mb-2">
                    <stat.icon className="w-4 h-4 text-orange-600 md:w-5 md:h-5" />
                  </div>
                  <p className="text-sm font-bold text-gray-900 md:text-xl">
                    {stat.value}
                  </p>
                  <p className="text-[10px] font-medium text-gray-600 md:text-sm">
                    {stat.label}
                  </p>
                  <p className="text-[8px] text-gray-400 mt-0.5 hidden sm:block md:text-xs">
                    {stat.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section - Improved cards */}
        <section ref={featuresRef} className="py-10 bg-gray-50 md:py-16">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="mb-8 text-center md:mb-12">
              <span className="inline-block px-3 py-1 mb-2 text-xs font-semibold text-orange-600 bg-orange-100 rounded-full md:px-4 md:py-1.5 md:text-sm md:mb-3">
                Why Choose Us
              </span>
              <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
                Everything You Need to Succeed
              </h2>
              <p className="max-w-md mx-auto mt-2 text-sm text-gray-600 sm:text-base md:mt-3">
                Comprehensive features designed to accelerate your learning
                journey
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
              {features.map((feature, idx) => (
                <Link
                  key={idx}
                  to={feature.link}
                  className="group p-5 transition-all bg-white border border-gray-100 shadow-sm feature-card rounded-xl hover:shadow-xl hover:-translate-y-1.5 hover:border-orange-200 md:p-6"
                >
                  <div
                    className={`w-11 h-11 mb-3 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center shadow-sm md:w-13 md:h-13 md:mb-4`}
                  >
                    <feature.icon className="w-5 h-5 text-white md:w-6 md:h-6" />
                  </div>
                  <h3 className="mb-1 text-sm font-bold text-gray-900 md:text-base md:mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-xs leading-relaxed text-gray-600 md:text-sm">
                    {feature.description}
                  </p>
                  <span className="inline-flex items-center gap-0.5 mt-2.5 text-xs font-medium text-orange-600 transition-all group-hover:gap-1.5 md:text-sm md:mt-3">
                    Learn More{" "}
                    <ChevronRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Courses Section */}
        <Courses />

        {/* Mentorship Banner - Improved typography */}
        <section className="py-10 bg-gradient-to-r from-orange-600 to-orange-500 md:py-16">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="flex flex-col items-center justify-between gap-6 text-center lg:flex-row lg:text-left">
              <div>
                <h2 className="text-2xl font-bold text-white sm:text-3xl md:text-4xl">
                  Get Personalized Mentorship
                </h2>
                <p className="max-w-md mt-1 text-sm text-orange-100 sm:text-base md:mt-2">
                  Connect with industry experts for 1-on-1 guidance, career
                  advice, and portfolio reviews
                </p>
              </div>
              <Link
                to="/mentorship"
                className="inline-flex items-center gap-1.5 px-6 py-3 text-sm font-semibold text-orange-600 transition-all bg-white rounded-xl hover:shadow-lg hover:scale-105 md:px-7 md:py-3.5 md:text-base"
              >
                Find Your Mentor{" "}
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section - Improved cards */}
        <section className="py-10 bg-gray-50 md:py-16">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="mb-8 text-center md:mb-10">
              <span className="inline-block px-3 py-1 mb-2 text-xs font-semibold text-orange-600 bg-orange-100 rounded-full md:px-4 md:py-1.5 md:text-sm md:mb-3">
                Success Stories
              </span>
              <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
                What Our Students Say
              </h2>
              <p className="mt-2 text-sm text-gray-600 sm:text-base">
                Join thousands of successful learners who transformed their
                careers
              </p>
            </div>

            <div className="relative max-w-2xl mx-auto">
              {testimonials.map((testimonial, idx) => (
                <div
                  key={testimonial.id}
                  className={`transition-all duration-500 ${activeTestimonial === idx ? "block" : "hidden"}`}
                >
                  <div className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl md:p-8">
                    <Quote className="w-8 h-8 mb-4 text-orange-200 md:w-10 md:h-10 md:mb-5" />
                    <p className="mb-5 text-base leading-relaxed text-gray-700 md:text-lg md:mb-6">
                      "{testimonial.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-11 h-11 text-base font-bold text-white rounded-full bg-gradient-to-br from-orange-400 to-orange-600 md:w-13 md:h-13 md:text-lg">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 md:text-base">
                          {testimonial.name}
                        </p>
                        <p className="text-xs text-gray-500 md:text-sm">
                          {testimonial.role} at {testimonial.company}
                        </p>
                        {renderStars(testimonial.rating)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-center gap-1.5 mt-5 md:mt-6">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTestimonial(idx)}
                    className={`h-1.5 rounded-full transition-all md:h-2 ${
                      activeTestimonial === idx
                        ? "w-5 bg-orange-500 md:w-6"
                        : "w-1.5 bg-gray-300 md:w-2"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - Improved */}
        <section className="py-10 bg-white md:py-16">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="p-6 text-center bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl md:p-10">
              <h2 className="text-2xl font-bold text-white sm:text-3xl md:text-4xl">
                Ready to Start Your Learning Journey?
              </h2>
              <p className="max-w-md mx-auto mt-2 text-sm text-gray-300 sm:text-base md:mt-3">
                Join thousands of learners already mastering new skills on our
                platform
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-4 md:gap-4 md:mt-6">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-1.5 px-6 py-2.5 text-sm font-semibold text-white transition-all bg-orange-500 rounded-xl hover:bg-orange-600 hover:shadow-lg hover:scale-105 md:px-7 md:py-3 md:text-base"
                >
                  Get Started Free{" "}
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                </Link>
                <Link
                  to="/courses"
                  className="inline-flex items-center gap-1.5 px-6 py-2.5 text-sm font-semibold text-white transition-all border rounded-xl hover:bg-white/20 border-white/20 md:px-7 md:py-3 md:text-base"
                >
                  Browse Courses
                </Link>
              </div>
            </div>
          </div>
        </section>

        <style>{`
          @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0) translateX(0); }
            50% { transform: translateY(-15px) translateX(8px); }
          }
          .animate-fade-in-up {
            animation: fade-in-up 0.3s ease-out;
          }
          .animate-spin-slow {
            animation: spin-slow 8s linear infinite;
          }
          .animate-bounce-slow {
            animation: bounce-slow 2s ease-in-out infinite;
          }
          .animate-float {
            animation: float 6s ease-in-out infinite;
          }
          .animation-delay-500 {
            animation-delay: 0.5s;
          }
          .animation-delay-1000 {
            animation-delay: 1s;
          }
          @media (max-width: 640px) {
            .animate-bounce-slow {
              animation: none;
            }
          }
        `}</style>
      </main>
    </>
  );
};

export default Home;
